import type { SurveyDraftSection } from "@/components/features/surveys/SurveySectionEditor";
import type { SurveySection } from "../../../../shared/survey-questions";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import {
  useCreateMassTortSurvey,
  useUpdateMassTortSurvey,
} from "@/lib/data/react-query/mass-torts/mass-tort-survey";
import type {
  MassTortSurvey,
  MassTortSurveyQuestion,
} from "@/lib/data/requests/mass-torts/mass-tort-survey";
import type { APIError } from "@/lib/types/APIResponse";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import { isAxiosError } from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SurveyQuestionBuilder,
  getSurveyQuestionErrors,
  isSurveyChoice,
  type SurveyDraftQuestion,
} from "@/components/features/surveys/SurveyQuestionBuilder";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

function requestError(cause: unknown, fallback: string) {
  return isAxiosError<APIError>(cause)
    ? (cause.response?.data.meta.userMsg ?? fallback)
    : fallback;
}

function dateAfterDays(start: Date, days: number) {
  const date = new Date(start);
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function calendarDaysBetween(start: Date, end: string) {
  const parts = end.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) {
    return Number.NaN;
  }
  const [year, month, day] = parts;
  const selected = new Date(year, month - 1, day);
  if (
    selected.getFullYear() !== year ||
    selected.getMonth() + 1 !== month ||
    selected.getDate() !== day
  ) {
    return Number.NaN;
  }
  return (
    (Date.UTC(year, month - 1, day) -
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) /
    86_400_000
  );
}

function surveyDetailsSchema(dateOrigin: Date) {
  return z.object({
    title: z
      .string()
      .trim()
      .min(1, { message: "Title is required" })
      .max(200, { message: "Title must be 200 characters or less" }),
    description: z.string(),
    expiresAt: z.string().refine(
      (value) => {
        const days = calendarDaysBetween(dateOrigin, value);
        return Number.isInteger(days) && days >= 1 && days <= 36500;
      },
      { message: "Choose a date at least one day from today." },
    ),
  });
}

type SurveyDetailsValues = z.infer<ReturnType<typeof surveyDetailsSchema>>;

const surveyQuestionsSchema = z
  .array(
    z.object({
      prompt: z.string().trim().min(1),
      type: z.enum([
        "short_answer",
        "long_answer",
        "multiple_choice",
        "checkboxes",
        "date",
        "time",
        "datetime",
      ]),
      required: z.boolean(),
      options: z.array(z.object({ id: z.string(), label: z.string() })),
    }),
  )
  .min(1)
  .superRefine((questions, context) => {
    questions.forEach((question, index) => {
      if (
        isSurveyChoice(question.type) &&
        (question.options.length === 0 ||
          question.options.some((option) => !option.label.trim()))
      ) {
        context.addIssue({
          code: "custom",
          path: [index, "options"],
          message: "Complete every question and choice option.",
        });
      }
    });
  });

export function MassTortSurveyDetailsModal({
  massTortId,
  survey,
  savedQuestions = [],
  savedSections = [],
  canEdit = true,
  canDelete = false,
  onDelete,
  onClose,
  onSaved,
}: {
  massTortId: string;
  survey?: MassTortSurvey;
  savedQuestions?: MassTortSurveyQuestion[];
  savedSections?: SurveySection[];
  canEdit?: boolean;
  canDelete?: boolean;
  onDelete?: () => void;
  onClose: () => void;
  onSaved: (survey: MassTortSurvey) => void;
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const create = useCreateMassTortSurvey();
  const update = useUpdateMassTortSurvey(massTortId);
  const [dateOrigin] = useState(() => new Date());
  const form = useForm<SurveyDetailsValues>({
    resolver: zodResolver(surveyDetailsSchema(dateOrigin)),
    defaultValues: {
      title: survey?.title ?? "",
      description: survey?.description ?? "",
      expiresAt: dateAfterDays(dateOrigin, survey?.expirationDays ?? 7),
    },
    mode: "onSubmit",
  });
  const [requestErrorMessage, setRequestErrorMessage] = useState("");
  const [initialQuestions] = useState<SurveyDraftQuestion[]>(() =>
    savedQuestions.map((question) => ({ ...question, key: question.id })),
  );
  const [questions, setQuestions] = useState(initialQuestions);
  const [initialSections] = useState<SurveyDraftSection[]>(() =>
    savedSections.map((section) => ({
      ...section,
      key: section.id,
      persisted: true,
    })),
  );
  const [sections, setSections] = useState(initialSections);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const pending = submitting || update.isPending;

  const close = () => {
    if (pending) return;
    if (
      form.formState.isDirty ||
      JSON.stringify(questions) !== JSON.stringify(initialQuestions) ||
      JSON.stringify(sections) !== JSON.stringify(initialSections)
    ) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const save = async ({
    title,
    description,
    expiresAt,
  }: SurveyDetailsValues) => {
    const days = calendarDaysBetween(dateOrigin, expiresAt);
    const questionValidation = surveyQuestionsSchema.safeParse(questions);
    if (!questionValidation.success) {
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }
    if (
      sections.some((section) => !section.title.trim()) ||
      getSurveyQuestionErrors(questions, sections).some(
        (error) =>
          error.prompt ||
          error.missingSection ||
          error.missingOptions ||
          error.options.length > 0,
      )
    ) {
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }
    setValidationAttempt(0);
    const definitions = questions.map((question) => ({
      ...question,
      prompt: question.prompt.trim(),
      options: question.options.map((option) => ({
        ...option,
        label: option.label.trim(),
      })),
    }));
    setRequestErrorMessage("");
    setSubmitting(true);
    try {
      const result = survey
        ? await update.mutateAsync({
            surveyId: survey.id,
            dateModified: survey.dateModified,
            title: title.trim(),
            description: description.trim() || null,
            expirationDays: days,
            questions: definitions,
            sections,
          })
        : await create.mutateAsync({
            massTortId,
            title: title.trim(),
            description: description.trim() || null,
            expirationDays: days,
            questions: definitions,
            sections,
          });
      onSaved(result.data);
    } catch (cause) {
      setRequestErrorMessage(
        requestError(
          cause,
          "Could not save this survey. Your changes have not been saved.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <AppModal
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={survey ? "Edit survey" : "Create survey"}
        description={
          survey
            ? undefined
            : "Prepare the questions before creating the survey."
        }
        size="2xl"
        bodyContained
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div>
              {survey && canDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={onDelete}
                >
                  Delete survey
                </Button>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={close}
              >
                Cancel
              </Button>
              {canEdit ? (
                <Button
                  type="submit"
                  form="mass-tort-survey-details"
                  disabled={pending}
                >
                  {pending
                    ? "Saving..."
                    : survey
                      ? "Save changes"
                      : "Create survey"}
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        <div
          ref={setScrollElement}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
        >
          <form
            id="mass-tort-survey-details"
            className="space-y-5"
            noValidate
            onSubmit={form.handleSubmit((values) => void save(values))}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <TextFormField
                register={form.register("title")}
                label="Title *"
                disabled={pending || !canEdit}
                maxLength={200}
              />
              <DateFormField
                name="expiresAt"
                register={form.register("expiresAt")}
                label="Expires *"
                minDate={new Date(dateAfterDays(dateOrigin, 1) + "T00:00:00")}
                clearable={false}
                isDisabled={pending || !canEdit}
              />
            </div>
            <TextAreaFormField
              register={form.register("description")}
              label="Description"
              disabled={pending || !canEdit}
            />
            <SurveyQuestionBuilder
              scrollElement={scrollElement}
              questions={questions}
              sections={sections}
              onSectionsChange={setSections}
              onChange={setQuestions}
              disabled={pending || !canEdit}
              validationAttempt={validationAttempt}
            />
            {requestErrorMessage ? (
              <p role="alert" className="text-destructive text-sm">
                {requestErrorMessage}
              </p>
            ) : null}
          </form>
        </div>
      </AppModal>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        buttonText="Discard changes"
        afterActionFn={onClose}
      />
    </Form>
  );
}
